/**
 * Owner: Masa Bayu
 * File: face-line-edit-commit.ts
 * Date:Time: 2026-10-15 08:25
 * Description: R11.5 · v2 line edit commit · measureLineEdit · atomic QXW.
 */

import {
  measureLineEdit,
  type Font,
  type MeasureLineEditError,
  type LinePaintMeta,
  type QXWLine,
} from '@qx/qx-line-paint';
import {
  isLinePaint02,
  type QXWDocument,
  type QXTextElementLinePaint,
} from '@qx/qxw-core';
import { getRenderPipe, isLatinLinePaintAllow } from '../feature-flags';
import { applyFaceTextEdit } from './qxeditor-face-edit';

export type FaceLineEditCommitOk = {
  readonly ok: true;
  readonly document: QXWDocument;
  readonly line: QXWLine;
};

export type FaceLineEditCommitFail = {
  readonly ok: false;
  readonly error: MeasureLineEditError['error'];
};

export type FaceLineEditCommitResult =
  | FaceLineEditCommitOk
  | FaceLineEditCommitFail;

export type FaceLineEditCommitInput = {
  readonly document: QXWDocument;
  readonly elementId: string;
  readonly editedText: string;
  /**
   * Tip line geometry + prior linePaint · required for v2 measure.
   * Absent under v2 → fail-closed EMPTY (no invent layout).
   */
  readonly tipLine?: QXWLine;
  readonly fontProvider?: (family: string) => Font | null;
};

const PX_PER_PT = 96 / 72;

let sharedCtx: CanvasRenderingContext2D | null | undefined;

function canvasCtx(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (sharedCtx === undefined) {
    try {
      sharedCtx = document.createElement('canvas').getContext('2d');
    } catch {
      sharedCtx = null;
    }
  }
  return sharedCtx ?? null;
}

/** Browser canvas font port · family CSS string · width in pt. */
export function defaultFaceLineFontProvider(
  family: string,
): Font | null {
  const ctx = canvasCtx();
  if (!ctx) return null;
  return {
    measureWidth(text: string, sizePt: number): number {
      ctx.font = `${String(sizePt)}pt ${family}`;
      return ctx.measureText(text).width / PX_PER_PT;
    },
  };
}

/**
 * measureLineEdit emits LinePaintMeta (paint package).
 * Tip write requires QXTextElementLinePaint (qxw-core) · LINE-01 only here.
 */
function toQXTextLinePaint01(
  linePaint: LinePaintMeta | undefined,
): QXTextElementLinePaint | undefined {
  if (linePaint === undefined) return undefined;
  if (linePaint.extension === 'R11-LINE-02') return undefined;

  const script =
    linePaint.script === 'latin' ||
    linePaint.script === 'arabic' ||
    linePaint.script === 'cjk' ||
    linePaint.script === 'mixed' ||
    linePaint.script === 'other'
      ? linePaint.script
      : undefined;
  const direction =
    linePaint.direction === 'ltr' ||
    linePaint.direction === 'rtl' ||
    linePaint.direction === 'ttb'
      ? linePaint.direction
      : undefined;

  return {
    ...(linePaint.wordStarts !== undefined
      ? { wordStarts: linePaint.wordStarts }
      : {}),
    ...(linePaint.wordGaps !== undefined ? { wordGaps: linePaint.wordGaps } : {}),
    ...(direction !== undefined ? { direction } : {}),
    ...(script !== undefined ? { script } : {}),
    ...(linePaint.role !== undefined ? { role: linePaint.role } : {}),
    ...(linePaint.column !== undefined ? { column: linePaint.column } : {}),
  };
}

function applyTextAndLinePaint(
  document: QXWDocument,
  elementId: string,
  text: string,
  linePaint: QXWLine['linePaint'],
): QXWDocument {
  const element = document.elements.find((item) => item.id === elementId);
  if (!element || element.type !== 'text') return document;

  const withText = applyFaceTextEdit(document, elementId, text);
  const idx = withText.elements.findIndex((item) => item.id === elementId);
  const current = withText.elements[idx];
  if (idx < 0 || !current || current.type !== 'text') return withText;

  const tipPaint = toQXTextLinePaint01(linePaint);
  const elements = withText.elements.slice();
  elements[idx] = {
    ...current,
    ...(tipPaint !== undefined ? { linePaint: tipPaint } : {}),
  };
  return { ...withText, elements };
}

/**
 * v2: measureLineEdit → atomic text + linePaint · bbox preserved.
 * v1/legacy: applyFaceTextEdit only (tipLine ignored).
 * Fail → ok:false · caller restores DOM · document unchanged (G46/G47).
 */
export function commitFaceLineEdit(
  input: FaceLineEditCommitInput,
): FaceLineEditCommitResult {
  const pipe = getRenderPipe();
  const lp = input.tipLine?.linePaint;
  // R11.7e · LINE-02 has no redistribute yet → v1 text write
  // LINE-01 + Latin + wordStarts → R11.5 measure (hold path)
  const useV2Measure =
    pipe === 'v2' &&
    isLatinLinePaintAllow(lp) &&
    lp?.extension !== 'R11-LINE-02' &&
    lp?.wordStarts !== undefined;
  if (!useV2Measure) {
    const document = applyFaceTextEdit(
      input.document,
      input.elementId,
      input.editedText,
    );
    return {
      ok: true,
      document,
      line: {
        id: input.elementId,
        text: input.editedText,
        layout: input.tipLine?.layout ?? { x: 0, y: 0, width: 0, height: 0 },
        typography: input.tipLine?.typography ?? { family: '', size: 0 },
        ...(input.tipLine?.linePaint !== undefined
          ? { linePaint: input.tipLine.linePaint }
          : {}),
      },
    };
  }

  const tip = input.tipLine;
  if (!tip || tip.id !== input.elementId) {
    return { ok: false, error: 'EMPTY' };
  }

  const fontProvider =
    input.fontProvider ?? defaultFaceLineFontProvider;
  const measured = measureLineEdit(tip, input.editedText, fontProvider);
  if ('error' in measured) {
    return { ok: false, error: measured.error };
  }

  // G47 · single document write · text + linePaint together.
  const document = applyTextAndLinePaint(
    input.document,
    input.elementId,
    measured.text,
    measured.linePaint,
  );

  const after = document.elements.find((el) => el.id === input.elementId);
  const afterPaint =
    after?.type === 'text' && after.linePaint !== undefined && !isLinePaint02(after.linePaint)
      ? after.linePaint
      : undefined;
  if (
    !after ||
    after.type !== 'text' ||
    afterPaint === undefined ||
    JSON.stringify(afterPaint.wordStarts) !==
      JSON.stringify(measured.linePaint?.wordStarts)
  ) {
    return { ok: false, error: 'OVERFLOW' };
  }

  return { ok: true, document, line: measured };
}

export function logFaceLineEditFail(input: {
  readonly lineId: string;
  readonly error: MeasureLineEditError['error'];
}): void {
  console.warn(
    `[R11.5] line edit FAIL-CLOSED · ${input.lineId} · ${input.error}`,
  );
}
